import styled, { css } from 'styled-components';
import { Form as FormikForm, ErrorMessage, Field } from 'formik';
import { m } from 'framer-motion';
import { rgba } from 'polished';

import Button from '../../atoms/Button';
import { positionFullStyles, transition } from '../../../common/mixins';
import Typography from '../../styles/Typography';
import { FlexBox } from '../../styles/Grid/styles';
import media from '../../../common/MediaQueries';
import { FormBtnStyledProps, FormRowStyledProps, FormMessageStyledProps } from './types';

const fieldBaseStyles = css`
  position: relative;
  width: 100%;
  padding: 0.875rem 1.75rem 0.875rem 0;
  border: 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.additional.accentGray2};
  background: transparent;
  color: inherit;

  &::placeholder {
    ${transition()}
  }
  &:focus {
    outline: 0;
    border-color: ${({ theme }) => theme.colors.additional.accentGray4};
  }
  &:focus::placeholder {
    opacity: 0;
  }
`;

export const FormBox = styled(FormikForm)`
  position: relative;
  display: flex;
  background-color: #fff;
  flex-direction: column;
  color: ${({ theme }) => theme.colors.additional.accentGray2};
`;

export const Form = styled.form`
  position: relative;
  display: flex;
  flex-direction: column;
  color: ${({ theme }) => theme.colors.additional.accentGray2};
`;

export const FormError = styled(ErrorMessage)`
  position: absolute;
  bottom: -0.25rem;
  transform: translateY(100%);
  color: ${({ theme }) => theme.colors.danger.main};
  ${({ theme }) => theme.textS}
`;

export const FormLabel = styled(FlexBox)`
  position: relative;
  align-items: center;
`;

export const FormLabelText = styled(Typography)`
  padding-left: 0.5rem;
  cursor: pointer;
`;

export const FormTextarea = styled.textarea`
  ${fieldBaseStyles}
  height: 7rem;
  padding: 1rem 1.75rem 1rem 1rem;
  border-radius: 0.25rem;
  border: 1px solid ${({ theme }) => theme.colors.additional.accentGray2};
`;

const formFieldStyles = css`
  &[type='checkbox'] {
    width: 1.25rem;
    min-width: 1.25rem;
    height: 1.25rem;
    cursor: pointer;
    z-index: 1;
    visibility: hidden;

    ~ ${FormLabelText}::before, ~ ${FormLabelText}::after {
      position: absolute;
      content: '';
      border-radius: 50%;
    }
    ~ ${FormLabelText}::before {
      top: 0;
      left: 0;
      width: 1.25rem;
      height: 1.25rem;
      border: 1px solid ${({ theme }) => theme.colors.additional.accentGray1};
    }
    ~ ${FormLabelText}::after {
      ${transition()}
      top: 0.3125rem;
      left: 0.3125rem;
      width: 0.75rem;
      height: 0.75rem;
      background-color: ${({ theme }) => theme.colors.secondary.hover};
      opacity: 0;
    }
    &:checked ~ ${FormLabelText}::after {
      opacity: 1;
    }
  }

  &[type='search'] {
    height: 3.5rem;
    padding: 1rem 1rem 1rem 3.875rem;
    border-bottom: 0;
    border-radius: 6.25rem;
    background-color: ${({ theme }) => theme.colors.additional.accentGray3};
    &:focus {
      box-shadow: 0px 0px 4px -1px ${({ theme }) => theme.colors.additional.accentGray4};
    }
  }
`;

export const FormField = styled(Field)`
  ${fieldBaseStyles}
  ${formFieldStyles}
`;

export const FormInput = styled.input`
  ${fieldBaseStyles}
  ${formFieldStyles}
`;

export const FormBtn = styled(Button) <FormBtnStyledProps>`
  margin-top: 2.5rem;

  &[aria-disabled='true'] {
    opacity: 0.5;
  }

  ${({ isSearch }) =>
    isSearch &&
    css`
      position: absolute;
      top: calc(50% - (1.25rem / 2));
      left: 1.5rem;
      font-size: 1.25rem;
      height: 1.25rem;
      width: 1.25rem;
      margin-top: 0;
      color: ${({ theme }) => theme.colors.secondary.hover};
    `}
`;

export const SearchIconWrapper = styled.div`
  position: absolute;
  top: calc(50% - (1.25rem / 2));
  left: 1.5rem;
  font-size: 1.25rem;
  height: 1.25rem;
  width: 1.25rem;
  color: ${({ theme }) => theme.colors.secondary.hover};
`;

export const FormRow = styled.div<FormRowStyledProps>`
  position: relative;
  width: 100%;

  &:not(:first-child) {
    margin-top: 2rem;
  }

  ${({ hasIcon }) =>
    hasIcon &&
    css`
      &::before {
        display: none;
        position: absolute;
        content: '';
        width: 0.875rem;
        height: 0.875rem;
        background-size: contain;
        border-radius: 50%;
        right: 0.625rem;
        top: 1.125rem;
        z-index: 1;
      }
    `}

  ${({ hasError }) =>
    hasError &&
    css`
      &::before {
        display: block;
        background-image: url('/svg/icons/icon-error.svg');
      }
      ${FormTextarea},
      ${FormField} {
        padding-left: 1rem;
        border-radius: 0.25rem;
        border: 1px solid ${({ theme }) => theme.colors.danger.main};
        background-color: ${({ theme }) => theme.colors.neutral.neutral20};
      }
      ${FormField}[type='checkbox'] ~ ${FormLabelText}::before {
        border-color: ${({ theme }) => theme.colors.danger.main};
      }
    `}

  ${({ isValidated }) =>
    isValidated &&
    css`
      &::before {
        display: block;
        background-image: url('/svg/icons/icon-success.svg');
      }
    `}
`;

export const FormLoader = styled(m.div)`
  ${positionFullStyles}
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
  background-color: ${({ theme }) => rgba(theme.colors.neutral.neutral10, 0.5)};
`;

export const FormMessage = styled(m.div) <FormMessageStyledProps>`
  position: absolute;
  bottom: -0.75rem;
  transform: translateY(100%);
  color: ${({ hasSucceed, theme }) =>
    hasSucceed ? theme.colors.success.main : theme.colors.danger.main};
  ${({ theme }) => theme.textS};

  @media ${media.phone} {
    bottom: -1rem;
  }
`;
